import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { MenuOptionValue, Paginated, PaginationQuery, PaginationQueryWithFields } from "../../types";
import useApi, { buildUrl } from "../api";
import { CustomError } from "../errors";
import { TakeInChargeRequestBody } from "../processingRequestHistory/types";
import {
  InsertProcessingRequest,
  ItemGroupedProcessingRequests,
  ProcessingRequest,
  UpdateProcessingRequest,
  UpdateRequestAvailabilityDate,
  UpdateRequestMinutes,
} from "./types";

export interface FileInfo {
  data: string;
  contentType: string;
}

const useProcessingRequestApi = () => {
  const baseurl = "/processing_requests";
  const historyUrl = "/request_history";
  const itemUrl = "/items";

  const api = useApi();

  const useProcessingRequestDetail = useCallback(
    (processingRequestID: string) => {
      const url = `${baseurl}/${processingRequestID}`;
      const result = useQuery<ProcessingRequest | null, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<ProcessingRequest>({ url, signal }),
        enabled: false,
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api],
  );

  const useProcessingRequest = useCallback(
    (query: PaginationQueryWithFields<ProcessingRequest>, views: string[]) => {
      const params = new URLSearchParams();
      views.forEach((v) => params.append("views", v));

      const url = buildUrl(`${baseurl}?${params.toString()}`, query);
      const result = useQuery<Paginated<Partial<ProcessingRequest>>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<Partial<ProcessingRequest>>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api],
  );

  const useItemGroupedRequest = useCallback(
    (query: PaginationQuery) => {
      const url = buildUrl(baseurl + itemUrl, query);
      const result = useQuery<Paginated<ItemGroupedProcessingRequests>, CustomError>({
        queryKey: [url],
        queryFn: ({ signal }) => api.get<Paginated<ItemGroupedProcessingRequests>>({ url, signal }),
      });
      return { ...result, error: result.error ?? undefined };
    },
    [api],
  );

  const createProcessingRequest = useCallback(
    async (pr: InsertProcessingRequest) => {
      const data = await api.post<InsertProcessingRequest, ProcessingRequest>(baseurl, pr);
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  // const createProcessingRequestForKit = useCallback(
  //   async (pr: InsertProcessingRequestForKit) => {
  //     const data = await api.post<InsertProcessingRequestForKit, ProcessingRequest>(
  //       baseurl + "/create_kit_request",
  //       pr,
  //     );
  //     api.mutateCache([baseurl]);
  //     return data;
  //   },
  //   [api],
  // );

  const updateProcessingRequest = useCallback(
    async (pr: UpdateProcessingRequest, processingRequestID: number) => {
      const data = await api.put<UpdateProcessingRequest>(`${baseurl}/${processingRequestID}`, pr);
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  const updateRequestMinutes = useCallback(
    async (pr: UpdateRequestMinutes, processingRequestID: number) => {
      const data = await api.put<UpdateRequestMinutes>(`${baseurl}/${processingRequestID}/update_minutes`, pr);
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  const updateRequestAvailability = useCallback(
    async (pr: UpdateRequestAvailabilityDate, processingRequestID: number) => {
      const data = await api.put<UpdateRequestAvailabilityDate>(
        `${baseurl}/${processingRequestID}/update_availability`,
        pr,
      );
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  const deleteProcessingRequest = useCallback(
    async (processingRequestID: number) => {
      const data = await api.del(`${baseurl}/${processingRequestID}`);
      api.mutateCache([baseurl]);
      return data;
    },
    [api],
  );

  const getFieldFiltersQuery = useCallback(
    (
      fieldName: string,
      views: string[],
      query?: PaginationQuery | PaginationQueryWithFields<ProcessingRequest>,
    ) => {
      const viewsParams = new URLSearchParams();
      views.forEach((v) => viewsParams.append("views", v));
      const url = query
        ? buildUrl(`${baseurl}/filters?field=${fieldName}&${viewsParams.toString()}`, query)
        : `${baseurl}/filters?field=${fieldName}&${viewsParams.toString()}`;
      return {
        queryKey: [url],
        queryFn: ({ signal }: { signal: AbortSignal }) => api.get<MenuOptionValue[]>({ url, signal }),
      };
    },
    [api],
  );

  const takeInChargeRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/take_in_charge`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const sendOfferRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/send_offer`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const acceptRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/accept`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const refuseRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/refuse`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const discardRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/discard`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const startRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/start`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const finishRequest = useCallback(
    async (takeInCharge: TakeInChargeRequestBody, processingRequestID: number) => {
      const data = await api.post<TakeInChargeRequestBody, null>(
        `${baseurl}/${processingRequestID}/finish`,
        takeInCharge,
      );
      api.mutateCache([baseurl + "/" + processingRequestID + historyUrl]);
      return data;
    },
    [api],
  );

  const exportExcel = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(baseurl, query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  const exportExcelGroupedRequests = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(baseurl + itemUrl, query);
      const h = new Headers();
      h.append("Accept", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      const response = api.get<FileInfo>({ url, signal, headers: h });
      return response;
    },
    [api],
  );

  return {
    useProcessingRequestDetail,
    useProcessingRequest,
    useItemGroupedRequest,
    createProcessingRequest,
    // createProcessingRequestForKit,
    updateProcessingRequest,
    updateRequestMinutes,
    updateRequestAvailability,
    deleteProcessingRequest,
    getFieldFiltersQuery,
    takeInChargeRequest,
    sendOfferRequest,
    acceptRequest,
    refuseRequest,
    discardRequest,
    startRequest,
    finishRequest,
    exportExcel,
    exportExcelGroupedRequests,
  };
};

export default useProcessingRequestApi;
